import { memo, useMemo, useState } from "react";
import { CalendarOutlined, ExportOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Collapse, Empty, List, Popconfirm, Space, Tag, Typography } from "antd";
import orderBy from "lodash.orderby";
import { useTranslation } from "react-i18next";

import { programAutomationActions } from "@fitness-app/app-store";
import { toAiTrainingPrograms, type WorkoutRoutine } from "@fitness-app/data-models/entities/ProgramAutomation";

const hasMatchRules = (routine: WorkoutRoutine) =>
  Boolean(
    routine.matchRules &&
      Object.values(routine.matchRules).some((rule) => Array.isArray(rule) && rule.length > 0),
  );

import {
  AddTrainingScheduleForm,
  AiTrainingProgramSection,
} from "~/modules/Automation/ProgramAutomation/components/AiAutomationCards/AiAutomationCards";
import ModalForm from "~/components/ModalForm/ModalForm";
import ScheduleProgramStepper, {
  useScheduleProgramStepper,
} from "~/modules/Automation/ScheduleProgramStepper/ScheduleProgramStepper";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

const ScheduleTrainingPrograms = () => {
  const { t } = useTranslation(["automation", "common"]);
  const { visible, closeModal, openModal, routineModel } = useScheduleProgramStepper();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const selectedAutomation = useAppSelector((store) => store.programAutomation.selectedAutomation);
  const dispatch = useAppDispatch();
  const sortedScheduledRoutine = useMemo(
    () => orderBy(selectedAutomation?.scheduledWorkoutsRoutine || [], ["startWeek"]),
    [selectedAutomation?.scheduledWorkoutsRoutine],
  );

  if (!selectedAutomation) {
    return null;
  }

  const removeScheduledProgram = (routineId: string) => {
    const updatedScheduledWorkouts = selectedAutomation.scheduledWorkoutsRoutine.filter(
      (item) => item.routineId !== routineId,
    );
    void dispatch(
      programAutomationActions.updateAutomationTemplate({
        automationId: selectedAutomation.id,
        automation: {
          scheduledWorkoutsRoutine: updatedScheduledWorkouts,
        },
      }),
    );
  };

  const onSubmitScheduledProgram = async (workoutRoutine: WorkoutRoutine) => {
    if (routineModel) {
      await dispatch(
        programAutomationActions.updateAutomationTemplate({
          automationId: selectedAutomation.id,
          automation: {
            scheduledWorkoutsRoutine: [
              ...(selectedAutomation.scheduledWorkoutsRoutine ?? []).map((item) =>
                item.routineId !== routineModel.routineId ? item : workoutRoutine,
              ),
            ],
          },
        }),
      );
    } else {
      await dispatch(
        programAutomationActions.updateAutomationTemplate({
          automationId: selectedAutomation.id,
          automation: {
            scheduledWorkoutsRoutine: [...(selectedAutomation.scheduledWorkoutsRoutine ?? []), workoutRoutine],
          },
        }),
      );
    }
  };

  return (
    <>
      <Collapse>
        <Collapse.Panel key="programs" header={t("trainings.plannedAutomation")}>
          <List
            itemLayout="horizontal"
            bordered={false}
            locale={{
              // With an AI entry configured the big empty-state is noise — the AI row
              // below is the plan; keep only a compact add button.
              emptyText: toAiTrainingPrograms(selectedAutomation).length ? (
                <div />
              ) : (
                <Empty description={t("trainings.emptyState")}>
                  <Button icon={<PlusOutlined />} type="primary" onClick={() => setAddModalOpen(true)}>
                    {t("trainings.addPlan")}
                  </Button>
                </Empty>
              ),
            }}
            dataSource={sortedScheduledRoutine}
            footer={
              sortedScheduledRoutine.length ? (
                <div className="flex items-center justify-center">
                  <Button icon={<PlusOutlined />} size="small" type="primary" onClick={() => setAddModalOpen(true)}>
                    {t("trainings.addAnotherPlan")}
                  </Button>
                </div>
              ) : null
            }
            renderItem={(routine) => (
              <List.Item
                actions={[
                  <Typography.Link key="edit" onClick={() => openModal(routine)}>
                    edytuj
                  </Typography.Link>,
                  <Popconfirm
                    key="remove"
                    title={t("trainings.confirmDelete")}
                    okText={t("common:yes")}
                    cancelText={t("common:no")}
                    placement="left"
                    onConfirm={() => removeScheduledProgram(routine.routineId)}
                  >
                    <Typography.Link type="danger">{t("common:button.delete")}</Typography.Link>
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  title={
                    <div className="flex items-center gap-1">
                      <Typography.Text strong>{routine.name}</Typography.Text>
                      <Button
                        icon={<ExportOutlined />}
                        title="Otwórz w nowy oknie"
                        href={`${window.location.origin}/programs/plans/${routine.programId || routine.id}`}
                        target="_blank"
                        type="link"
                      />
                    </div>
                  }
                  description={
                    <Space direction="horizontal" size={8} wrap>
                      <CalendarOutlined />
                      <Typography.Text type="secondary">
                        {routine.startWeek !== routine.endWeek
                          ? t("trainings.fromTo", {
                              weekFrom: routine.startWeek,
                              weekTo: routine.endWeek,
                            })
                          : t("trainings.from", { week: routine.startWeek })}
                      </Typography.Text>
                      {hasMatchRules(routine) ? (
                        <Tag color="purple">wariant dopasowany do profilu</Tag>
                      ) : null}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
                  <AiTrainingProgramSection />
          {toAiTrainingPrograms(selectedAutomation).length && !sortedScheduledRoutine.length ? (
            <div className="mt-4 flex items-center justify-center">
              <Button icon={<PlusOutlined />} size="small" type="primary" onClick={() => setAddModalOpen(true)}>
                {t("trainings.addAnotherPlan")}
              </Button>
            </div>
          ) : null}
        </Collapse.Panel>
      </Collapse>
      <ModalForm
        open={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        title={t("trainings.addPlan")}
        width={760}
      >
        <AddTrainingScheduleForm
          onTemplateChosen={() => {
            setAddModalOpen(false);
            openModal();
          }}
          onAiSaved={() => setAddModalOpen(false)}
        />
      </ModalForm>
      {selectedAutomation && (
        <ScheduleProgramStepper
          visible={visible}
          handleClose={closeModal}
          routineModel={routineModel || undefined}
          selectedAutomation={selectedAutomation}
          onSubmit={onSubmitScheduledProgram}
        />
      )}
    </>
  );
};

export default memo(ScheduleTrainingPrograms);
